import tinycolor from 'tinycolor2'
import BRAND_SHADES from './brand-shades.json'

/**
 * The accent themes an admin can pick from, and the maths that turns one into
 * the CSS custom properties the stylesheets read.
 *
 * A preset is only the colours a designer chooses. Every tint, the hover /
 * active / disabled gradients and the sidebar pastel are derived from them
 * below, so adding a theme means adding five colours. `darkPrimary` is the
 * tone for text and icons in dark mode: the light-mode primaries are too dark
 * to read on a dark surface. WCAG contrast for each preset: THEME_PLAN.md §2.
 *
 * core-web keeps a copy (src/theme/presets.js) so a community's public page
 * shows the accent chosen here: change both together.
 */

export const DEFAULT_ACCENT = 'coredirection'

// 'light' until dark mode has been swept page by page (THEME_PLAN.md, Phase 3).
export const DEFAULT_MODE = 'light'

export const MODES = ['light', 'dark', 'system']

// A community's own brand colour (Settings → Appearance), stored as
// { accent: 'custom', custom: { primary } }; see deriveAccent.
export const CUSTOM_ACCENT = 'custom'

// Listed in picker order: the header shows six per row, so the first row runs
// through the cool hues and the second through the warm ones, ending on the
// two neutrals. Saved themes are keyed by name, so reordering is safe.
export const ACCENTS = {
  coredirection: {
    primary: '#690FAD',
    secondary: '#CAA8F5',
    gradient: ['#6D28D9', '#B5179E', '#F15B8F'],
    darkPrimary: '#B57BEE',
    // Hand-tuned in style.scss before presets existed. Kept verbatim so the
    // default theme does not shift; the other presets derive these.
    gradientHover: ['#7E3AF2', '#CB1FAE', '#FF6FA1'],
    gradientActive: ['#5B21B6', '#9D1486', '#D94A78'],
    gradientDisabled: ['#CBBDE6', '#E2B9CF'],
    sidebar: ['#EDE5FA', '#F8EAF6', '#FDEAF0'],
  },
  royal: {
    primary: '#3730A3',
    secondary: '#C4C1F8',
    gradient: ['#1E3A8A', '#4338CA', '#7C3AED'],
    darkPrimary: '#A5A8FF',
  },
  ocean: {
    primary: '#1D4ED8',
    secondary: '#A7C1F7',
    gradient: ['#1E40AF', '#2563EB', '#0284C7'],
    darkPrimary: '#7AA7FF',
  },
  // Northern lights: green through cyan into violet.
  aurora: {
    primary: '#0E7490',
    secondary: '#A6E3EC',
    gradient: ['#047857', '#0E7490', '#6D28D9'],
    darkPrimary: '#5CD5E8',
  },
  emerald: {
    primary: '#047857',
    secondary: '#9FDCC6',
    gradient: ['#065F46', '#047857', '#0F766E'],
    darkPrimary: '#4FD1A5',
  },
  forest: {
    primary: '#166534',
    secondary: '#B4E3C2',
    gradient: ['#064E3B', '#166534', '#4D7C0F'],
    darkPrimary: '#6EE7A0',
  },
  gold: {
    primary: '#A16207',
    secondary: '#F3D99B',
    gradient: ['#78350F', '#A16207', '#B7791F'],
    darkPrimary: '#F2C14E',
  },
  sunset: {
    primary: '#C2410C',
    secondary: '#F9C29C',
    gradient: ['#B45309', '#C2410C', '#DC2626'],
    darkPrimary: '#FB9A5B',
  },
  rose: {
    primary: '#BE123C',
    secondary: '#F5A8BD',
    gradient: ['#9F1239', '#BE123C', '#DB2777'],
    darkPrimary: '#FB7DA0',
  },
  plum: {
    primary: '#86198F',
    secondary: '#EDB9F2',
    gradient: ['#4A044E', '#86198F', '#C026D3'],
    darkPrimary: '#EBA3F5',
  },
  graphite: {
    primary: '#1F2937',
    secondary: '#C5CAD3',
    gradient: ['#111827', '#374151', '#4B5563'],
    darkPrimary: '#C9CED8',
  },
  // Warm black with a gold edge on the gradient.
  noir: {
    primary: '#1C1917',
    secondary: '#D6CFC4',
    gradient: ['#0C0A09', '#292524', '#A16207'],
    darkPrimary: '#E7D9B8',
  },
}

export const ACCENT_KEYS = Object.keys(ACCENTS)

// `percentage` is how much of `colour` survives; the rest is white / black.
const lightTint = (colour, percentage) => tinycolor.mix('white', colour, percentage).toHexString()
const darkTint = (colour, percentage) => tinycolor.mix('black', colour, percentage).toHexString()

const channels = colour => {
  const { r, g, b } = tinycolor(colour).toRgb()
  return { r, g, b }
}
const rgbTriplet = colour => {
  const { r, g, b } = channels(colour)
  return `${r} ${g} ${b}`
}

/**
 * One of the default theme's purple shades, recoloured for another primary.
 * The hue moves with the primary and the saturation scales with it. The
 * lightness follows the primary only near it, fading out toward white and
 * black, so a pale tint stays pale and a near-black stays near-black. A shade
 * keeps its place relative to the primary: Bootstrap's darken($primary) is
 * still a darker primary.
 */
function shiftShade(shade, from, to) {
  const s = tinycolor(shade).toHsl()
  const f = tinycolor(from).toHsl()
  const t = tinycolor(to).toHsl()
  const weight = s.l > f.l ? (1 - s.l) / (1 - f.l) : s.l / f.l
  return tinycolor({
    h: (s.h + t.h - f.h + 360) % 360,
    s: Math.min(1, f.s ? s.s * (t.s / f.s) : t.s),
    l: Math.min(1, Math.max(0, s.l + (t.l - f.l) * weight)),
  }).toHexString()
}

// Dark mode's card surface: every dark-mode brand colour is checked on it.
const DARK_SURFACE = '#12151C'

/**
 * Dark mode, for a brand colour used as a fill. A pale tint (a chip, a
 * selected row, the sidebar pastel) becomes a dark tint of the same hue, or it
 * would glare off the dark page; strong colours (buttons, the gradient) stay.
 */
function darkFill(colour) {
  const { h, s, l } = tinycolor(colour).toHsl()
  return l > 0.75 ? tinycolor({ h, s: s * 0.4, l: 0.1 + (1 - l) * 0.5 }).toHexString() : colour
}

/**
 * Dark mode, for a brand colour used as text or an icon: lightened until it
 * reads on the dark surface (WCAG AA, 4.5:1). #690FAD there is about 2:1.
 */
function darkInk(colour, ratio = 4.5) {
  const { h, s } = tinycolor(colour).toHsl()
  let { l } = tinycolor(colour).toHsl()
  let ink = tinycolor(colour)
  while (tinycolor.readability(ink, DARK_SURFACE) < ratio && l < 0.95) {
    l += 0.02
    ink = tinycolor({ h, s, l })
  }
  return ink.toHexString()
}

// Darkened in small steps until white text on it reaches `ratio`.
function darkenForWhiteText(colour, ratio) {
  const result = tinycolor(colour)
  while (tinycolor.readability(result, '#FFFFFF') < ratio && result.toHsl().l > 0.02) result.darken(2)
  return result
}

/**
 * A whole preset from one colour, for a community that brings its own brand
 * colour. It meets the rules the hand-picked presets meet (THEME_PLAN.md §2):
 * white button text on the primary and the gradient's first two stops at
 * 4.5:1 (3:1 on the lighter end, which carries bold labels only), the primary
 * as text on white, and a dark-mode ink as clear as the presets' (6:1).
 *
 * The gradient is built the way the presets are: a deeper, cooler start and a
 * brighter, warmer end around the colour itself. A colour too light for white
 * text is darkened until it qualifies; `adjusted` says so, and `primary` is
 * the colour actually used.
 */
export function deriveAccent(colour) {
  const requested = tinycolor(colour).toHexString()
  const primary = darkenForWhiteText(requested, 4.5)
  const { h, s, l } = primary.toHsl()
  // Greys have no hue to turn, and a pastel of one stays grey.
  const neutral = s < 0.12
  const stop = (turn, lift) => tinycolor({
    h: (h + (neutral ? 0 : turn) + 360) % 360,
    s,
    l: Math.min(0.9, Math.max(0.06, l + lift)),
  })
  const primaryHex = primary.toHexString()
  return {
    primary: primaryHex,
    secondary: tinycolor({ h, s: neutral ? s : Math.min(0.85, Math.max(0.3, s * 0.8)), l: 0.8 }).toHexString(),
    gradient: [
      darkenForWhiteText(stop(-12, -0.07), 4.5).toHexString(),
      primaryHex,
      darkenForWhiteText(stop(22, 0.08), 3).toHexString(),
    ],
    darkPrimary: darkInk(primaryHex, 6),
    adjusted: primaryHex !== requested,
  }
}

/** The preset for `accent`, with every derived colour filled in. */
export function resolveAccent(accent, custom) {
  const base = ACCENTS[accent] || ACCENTS[DEFAULT_ACCENT]
  const preset = accent === CUSTOM_ACCENT && custom && custom.primary
    ? deriveAccent(custom.primary)
    : base
  const [start, mid, end] = preset.gradient

  return {
    ...preset,
    // brighten / 85% of the colour reproduce the hand-tuned default within a
    // few points per channel, which is why they were picked.
    gradientHover: preset.gradientHover || preset.gradient.map(c => tinycolor(c).brighten(8).toHexString()),
    gradientActive: preset.gradientActive || preset.gradient.map(c => darkTint(c, 85)),
    gradientDisabled: preset.gradientDisabled || [start, end].map(c => tinycolor(lightTint(c, 35)).desaturate(10).toHexString()),
    // The brand stops at 12% / 9% / 13% over white: see --cd-sidebar.
    sidebar: preset.sidebar || [lightTint(start, 12), lightTint(mid, 9), lightTint(end, 13)],
  }
}

/**
 * The CSS custom properties for an accent in a mode, as { '--name': value }.
 *
 * The `--primary-*` / `--secondary-*` names and tint percentages are the ones
 * the old global theme mixin wrote, so every `$primary-*` / `$secondary-*`
 * SCSS alias (assets/scss/base/_colors.scss) keeps working unchanged.
 *
 * Colours that the stylesheets use as text also get a `-fg` variable
 * (postcss-theme-colours.js points `color`, `fill` and `stroke` at it). In
 * light mode it equals the colour; in dark mode it is the readable version.
 */
export function accentVariables(accent, custom, mode = 'light') {
  const preset = resolveAccent(accent, custom)
  const dark = mode === 'dark'
  const fill = colour => (dark ? darkFill(colour) : colour)
  const ink = colour => (dark ? darkInk(colour) : colour)
  const vars = {}

  const tints = (name, colour, inkColour) => {
    const light = fill(lightTint(colour, 30))
    const base = channels(fill(colour))
    const lightChannels = channels(light)
    vars[`--${name}-default`] = fill(colour)
    vars[`--${name}-default-fg`] = inkColour
    vars[`--${name}-default-fg-rgb`] = rgbTriplet(inkColour)
    vars[`--${name}-dark`] = fill(darkTint(colour, 60))
    vars[`--${name}-light`] = light
    vars[`--${name}-extra-light`] = fill(lightTint(colour, 15))
    vars[`--${name}-bg`] = fill(lightTint(colour, 6))
    vars[`--${name}-dark-fg`] = ink(darkTint(colour, 60))
    vars[`--${name}-light-fg`] = ink(lightTint(colour, 30))
    vars[`--${name}-extra-light-fg`] = ink(lightTint(colour, 15))
    vars[`--${name}-bg-fg`] = ink(lightTint(colour, 6))
    vars[`--${name}-default-r`] = base.r
    vars[`--${name}-default-g`] = base.g
    vars[`--${name}-default-b`] = base.b
    // Space-separated, for `rgb(var(--x-rgb) / .4)` and Tailwind's <alpha-value>.
    vars[`--${name}-default-rgb`] = `${base.r} ${base.g} ${base.b}`
    if (name === 'primary') {
      vars['--primary-light-r'] = lightChannels.r
      vars['--primary-light-g'] = lightChannels.g
      vars['--primary-light-b'] = lightChannels.b
    }
  }
  tints('primary', preset.primary, dark ? (preset.darkPrimary || darkInk(preset.primary)) : preset.primary)
  tints('secondary', preset.secondary, ink(preset.secondary))

  const stops = (name, colours, labels = ['start', 'mid', 'end']) => {
    colours.forEach((colour, i) => { vars[`--${name}-${labels[i]}`] = fill(colour) })
  }
  stops('cd-gradient', preset.gradient)
  stops('cd-gradient-hover', preset.gradientHover)
  stops('cd-gradient-active', preset.gradientActive)
  stops('cd-gradient-disabled', preset.gradientDisabled, ['start', 'end'])
  stops('cd-sidebar', preset.sidebar)
  vars['--cd-primary-on-dark'] = preset.darkPrimary || darkInk(preset.primary)
  const gradientLabels = ['start', 'mid', 'end']
  preset.gradient.forEach((colour, i) => {
    vars[`--cd-gradient-${gradientLabels[i]}-rgb`] = rgbTriplet(fill(colour))
    vars[`--cd-gradient-${gradientLabels[i]}-fg`] = ink(colour)
    vars[`--cd-gradient-${gradientLabels[i]}-fg-rgb`] = rgbTriplet(ink(colour))
  })

  // Every other purple the stylesheets use, Bootstrap's derived shades
  // included; the build points them at these (postcss-theme-colours.js). The
  // default accent keeps each one exactly.
  const defaultPrimary = ACCENTS[DEFAULT_ACCENT].primary
  const isDefault = preset.primary.toLowerCase() === defaultPrimary.toLowerCase()
  BRAND_SHADES.forEach(hex => {
    const colour = isDefault ? `#${hex}` : shiftShade(`#${hex}`, defaultPrimary, preset.primary)
    vars[`--cd-shade-${hex}`] = fill(colour)
    vars[`--cd-shade-${hex}-rgb`] = rgbTriplet(fill(colour))
    vars[`--cd-shade-${hex}-fg`] = ink(colour)
    vars[`--cd-shade-${hex}-fg-rgb`] = rgbTriplet(ink(colour))
  })

  return vars
}
